import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, ArrowLeft } from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import SEO from '@/components/shared/SEO';

// Section 1: Indoor/Outdoor Mat Solutions
const berberProducts = [
  {
    id: 'portico-nop',
    name: 'Portico Nop',
    image: '/Assets/images/custom-logo-mats/portico-nop-clm.jpg',
    description: 'Premium Berber construction offers exceptional durability and helps trap dirt effectively.'
  },
  {
    id: 'astoria-nop',
    name: 'Astoria Nop',
    image: '/Assets/images/custom-logo-mats/astoria-nop-clm.jpg',
    description: 'Classic berber style with enhanced durability for high-traffic areas.'
  },
  {
    id: 'premier-nop',
    name: 'Premier Nop',
    image: '/Assets/images/custom-logo-mats/premier-nop-clm.jpg',
    description: 'Our top-tier berber mat with superior color retention and longevity.'
  }
];

// Section 2: Nylon + Coco Carpet Mats
const nylonCocoProducts = [
  {
    id: 'kensington',
    name: 'Kensington',
    image: '/Assets/images/custom-logo-mats/kensington-clm.jpg',
    description: 'Premium nylon construction with luxurious appearance and performance.'
  },
  {
    id: 'natural-coco',
    name: 'Natural Coco/Coir',
    image: '/Assets/images/custom-logo-mats/natural-coco.jpg',
    description: 'Eco-friendly natural coir fibers with excellent scraping action.'
  },
  {
    id: 'synthetic-coco',
    name: 'Synthetic Coco',
    image: '/Assets/images/custom-logo-mats/synthetic-coco.jpg',
    description: 'Durable synthetic alternative with the look of natural coco.'
  }
];

// Section 3: Hybrid + Pinnacle H.D.
const hybridProducts = [
  {
    id: 'pinnacle-hd',
    name: 'Pinnacle H.D.',
    image: '/Assets/images/custom-logo-mats/pinnacle-clm.jpg',
    description: 'Heavy-duty outdoor performance with superior weather resistance.'
  },
  {
    id: '2-n-1',
    name: '2-N-1',
    image: '/Assets/images/custom-logo-mats/2n1-clm.jpg',
    description: 'Versatile dual-function mat combining scraping and moisture absorption.'
  },
  {
    id: 'safe-tread',
    name: 'Safe Tread Mats',
    image: '/Assets/images/custom-logo-mats/safe-tread-clm.jpg',
    description: 'Anti-slip surface designed for maximum safety in wet conditions.'
  }
];

// Customization Options
const customizationOptions = [
  { title: 'Full Color Printing', description: 'Photo-quality reproduction of logos and graphics' },
  { title: 'Custom Shapes', description: 'Die-cut to match your brand or space requirements' },
  { title: 'Size Flexibility', description: 'From 2x3 runners to 20+ foot entry systems' },
  { title: 'Material Selection', description: 'Nylon, rubber, vinyl, and specialty materials' },
  { title: 'Edge Options', description: 'Beveled, flat, or custom edge treatments' },
  { title: 'Backing Types', description: 'Rubber, vinyl, or gripper backing options' }
];

const CustomLogoMatsPage = () => {
  return (
    <Layout>
      <SEO 
        title="Custom Logo Mats"
        description="Design custom logo mats that make a lasting impression. Choose from berber carpet, nylon, rubber, and specialty materials. Perfect for branding your entrance and protecting your floors."
        canonicalUrl="/custom/logo-mats"
      />
      {/* Hero Section */}
      <section className="relative bg-primary-bg text-white py-20" data-testid="logo-mats-hero">
        <div className="container-portico">
          <Link to="/custom" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Custom Hub
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Custom Logo Mats
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Make a lasting first impression with branded floor mats that combine durability, 
              functionality, and stunning visual impact.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary-light"
                onClick={() => document.getElementById('quote-section').scrollIntoView({ behavior: 'smooth' })}
              >
                Request a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Link to="/custom/portfolio?category=logo-mats">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                  View Examples
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 1: Indoor/Outdoor Mat Solutions */}
      <section className="section-padding bg-background" data-testid="berber-section">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Indoor/Outdoor Mat Solutions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Classic berber and ribbed constructions known for exceptional durability and dirt trapping properties.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {berberProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-xl overflow-hidden shadow-lg border border-border hover:shadow-xl transition-shadow"
                data-testid={`berber-product-${product.id}`}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-xl mb-2">{product.name}</h3>
                  <p className="text-muted-foreground text-sm">{product.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2: Nylon + Coco Carpet Mats */}
      <section className="section-padding bg-muted" data-testid="nylon-coco-section">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Nylon + Coco Carpet Mats
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Premium nylon and natural coco/coir options for superior performance and eco-friendly solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {nylonCocoProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-xl overflow-hidden shadow-lg border border-border hover:shadow-xl transition-shadow"
                data-testid={`nylon-coco-product-${product.id}`}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-xl mb-2">{product.name}</h3>
                  <p className="text-muted-foreground text-sm">{product.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Hybrid + Pinnacle H.D. */}
      <section className="section-padding bg-background" data-testid="hybrid-section">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Hybrid + Pinnacle H.D.
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Heavy-duty outdoor solutions built for extreme conditions and maximum durability.
            </p>
          </motion.div>

          {/* Large outdoor image with product cards below */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden">
              <img
                src="/Assets/images/custom-logo-mats/hybrid-pinnacle-hero.jpg"
                alt="Outdoor Entry Solutions"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                  Built for the Elements
                </h3>
                <p className="text-white/80 max-w-2xl">
                  Our hybrid and heavy-duty outdoor mats withstand harsh weather while delivering 
                  exceptional performance and brand visibility.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {hybridProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-xl overflow-hidden shadow-lg border border-border hover:shadow-xl transition-shadow"
                data-testid={`hybrid-product-${product.id}`}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-xl mb-2">{product.name}</h3>
                  <p className="text-muted-foreground text-sm">{product.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Spectra Print HD Feature */}
      <section className="section-padding bg-secondary text-secondary-foreground" data-testid="spectra-section">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-medium mb-4">
                Featured Product
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Spectra Print HD
              </h2>
              <p className="text-lg text-secondary-foreground/90 mb-6">
                Our most advanced logo mat technology featuring high-definition printing 
                for photorealistic reproduction of your brand imagery.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Photo-Quality Printing</span>
                    <p className="text-sm text-secondary-foreground/80">Reproduce intricate logos, photos, and gradients with stunning clarity</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Unlimited Colors</span>
                    <p className="text-sm text-secondary-foreground/80">No color limitations - full spectrum printing capability</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Commercial-Grade Durability</span>
                    <p className="text-sm text-secondary-foreground/80">UV-stable inks with exceptional wear resistance</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Fast Turnaround</span>
                    <p className="text-sm text-secondary-foreground/80">Quick production times for urgent projects</p>
                  </div>
                </li>
              </ul>
              <Button 
                size="lg" 
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-secondary"
                onClick={() => document.getElementById('quote-section').scrollIntoView({ behavior: 'smooth' })}
              >
                Request Spectra Print Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="/Assets/images/custom-logo-mats/spectra-clm.jpg"
                alt="Spectra Print HD Logo Mat"
                className="rounded-xl shadow-xl"
              />
              <Link to="/products/spectra-print-hd" className="absolute -bottom-6 -right-6 bg-white text-primary px-6 py-3 rounded-lg shadow-lg hover:bg-gray-100 transition-colors">
                <span className="font-heading font-bold">Spectra Print HD</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Customization Options */}
      <section className="section-padding bg-muted">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Customization Options
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every detail matters. Our customization capabilities ensure your logo mats 
              perfectly represent your brand.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {customizationOptions.map((option, index) => (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-card p-6 rounded-xl border border-border"
              >
                <h3 className="font-heading font-semibold text-lg mb-2">{option.title}</h3>
                <p className="text-sm text-muted-foreground">{option.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-Location Section */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Multi-Location Rollouts
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Managing brand consistency across hundreds of locations? Our multi-location 
                program ensures every mat meets your exact specifications, delivered on time 
                and within budget.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Centralized Ordering</span>
                    <p className="text-sm text-muted-foreground">Single point of contact for all locations</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Inventory Management</span>
                    <p className="text-sm text-muted-foreground">We track and manage replacement schedules</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Direct-to-Store Shipping</span>
                    <p className="text-sm text-muted-foreground">Individual shipments to each location</p>
                  </div>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="/Assets/images/custom-logo-mats/multi-rollouts.jpg"
                alt="Multi-location rollout"
                className="rounded-xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section id="quote-section" className="section-padding bg-muted">
        <div className="container-portico text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contact us today for a custom quote on your logo mat project. 
              Our team will help you find the perfect solution for your needs.
            </p>
            <Link to="/custom#consultation-form">
              <Button size="lg" className="bg-primary hover:bg-primary/90">
                Request a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default CustomLogoMatsPage;
